import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { BuildingView } from "../../components/accommodation/building";
import { PageSkeleton } from "../../components/accommodation/page-skeleton";
import { useGamesTimeZone } from "../../components/travel-leg-form";
import { canManageAccommodation, canSeeBuildings } from "../../lib/accommodation/access";
import { useDefaultGamesId } from "../../lib/competition/games";
import { isYmd, todayIn } from "../../lib/travel/view";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);

/**
 * One building's floor map (her `#/accb/<id>`): a full page with the back
 * arrow. URL: `day` (games' zone; absent = today), `block`, `floor` — all
 * lenient. A hotel sees its own buildings, read-only (the API answers 404
 * for any other).
 */
export const Route = createFileRoute("/_app/accommodation/buildings/$buildingId")({
	validateSearch: z.object({
		day: lenient(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
		block: lenient(z.string().trim().min(1).max(40)),
		floor: lenient(z.coerce.number().int().min(-5).max(200)),
	}),
	beforeLoad: ({ context }) => {
		if (!canSeeBuildings(context.session)) throw redirect({ to: "/accommodation" });
	},
	component: BuildingPage,
});

function BuildingPage() {
	const { session } = Route.useRouteContext();
	const { buildingId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const gamesId = useDefaultGamesId(search.games_id);
	const timeZone = useGamesTimeZone(gamesId);
	if (!gamesId || !timeZone) return <PageSkeleton />;
	const today = todayIn(timeZone);
	return (
		<BuildingView
			key={buildingId}
			gamesId={gamesId}
			gamesIdInUrl={search.games_id}
			buildingId={buildingId}
			today={today}
			date={isYmd(search.day) ? search.day : today}
			onDate={(d) => void navigate({ search: (s) => ({ ...s, day: d === today ? undefined : d }), replace: true })}
			block={search.block}
			floor={search.floor}
			onPlace={(p) => void navigate({ search: (s) => ({ ...s, block: p.block, floor: p.floor }), replace: true })}
			canManage={canManageAccommodation(session)}
		/>
	);
}
